import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Hobbie } from "../schema/relation-fields.schema";
import {
  getHobbieDetail,
  getHobbiesSimilares,
} from "@/services/relation-fields.service";
import { SimilarRelationFieldsAlert } from "@/components/common/section-components/alert/SimilarRelationFieldsAlert";
import { handleUpdateHobbieHandler } from "../schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";

export const EditHobbiePage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();

  const { control, setValue } = useForm({
    resolver: zodResolver(
      z.object({
        nombre: z.any().optional(),
      }),
    ),
  });

  const { fetchPermissions } = useRoles()
  const [hobbie, setHobbie] = useState<Hobbie | undefined>();
  const [loadingHobbie, setLoadingHobbie] = useState<boolean>(false);

  const getHobbie = async (id: number) => {
    setLoadingHobbie(true);
    const response = await getHobbieDetail({ id });

    setHobbie(response);
    setLoadingHobbie(false);
  };

  useEffect(() => {
    if (isActive && id) {
      getHobbie(Number(id));
    }
  }, [isActive, id]);

  useEffect(() => {
    if (hobbie) {
      setValue("nombre", hobbie?.nombre);
    }
  }, [hobbie]);

  const watchedValues = useWatch({
    control,
  });

  const hasNoFilledValues = useMemo(() => {
    const { nombre } = watchedValues || {};

    return isEmpty(nombre);
  }, [watchedValues]);

  const [loadingUpdateHobbie, setLoadingUpdateHobbie] =
    useState<boolean>(false);
  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(AppRoutes.RELATION_FIELDS);
  };

  const [similarHobbies, setSimilarHobbies] = useState<Hobbie[] | undefined>(
    [],
  );

  const handleUpdateHobbie = () => {
    handleUpdateHobbieHandler({
      setLoadingUpdateHobbie,
      navigate,
      watchedValues,
      id: Number(id),
      fetchPermissions,
    });
  };

  const [loadingSimilarHobbies, setLoadingSimilarHobbies] =
    useState<boolean>(false);

  const getSimilarHobbies = async (nombre: string) => {
    setLoadingSimilarHobbies(true);
    const response = await getHobbiesSimilares({
      nombre,
      excluidos: [Number(hobbie?.id)],
    });

    setSimilarHobbies(response);

    setLoadingSimilarHobbies(false);
  };

  useEffect(() => {
    const { nombre } = watchedValues || {};
    if (!nombre) return;

    const timeout = setTimeout(() => {
      getSimilarHobbies(nombre);
    }, 500);

    return () => clearTimeout(timeout);
  }, [watchedValues?.nombre]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Editar hobbie"
        addButtonLabel="Confirmar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleUpdateHobbie}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingUpdateHobbie}
        loading={loadingUpdateHobbie || loadingHobbie}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                inputName="nombre"
                labelText="Nombre"
                control={control}
                disabled={loadingUpdateHobbie || loadingHobbie}
                placeholder="Ingresar nombre del hobbie"
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>

      {/* Hobbies similares */}
      {Boolean(similarHobbies?.length) && !isEmpty(watchedValues?.nombre) && (
        <SimilarRelationFieldsAlert
          title="Hay hobbies similares existentes"
          legend="Evite repeticiones"
          similarItems={similarHobbies?.map((el) => el.nombre) || []}
          loadingSimilarElements={loadingSimilarHobbies}
        />
      )}
    </Flex>
  );
};
